/**
 * What this browser keeps for ONE signed-in account, and the rule that keeps
 * it from reaching the next one.
 *
 * Chat history, recent translations, the last result, settings, progress
 * counters and a handful of caches live in localStorage under keys that do not
 * name an account. Signing out used to leave all of it behind: the next person
 * to sign in on the same browser saw it, and their first chat sync uploaded
 * the whole chat blob into THEIR account (the one-shot upload in lib/chatSync),
 * while the progress and settings syncs pushed the old values into theirs.
 * Three rules close that:
 *
 *  - an explicit sign-out clears it (clearAccountLocalState);
 *  - on load, state recorded for a DIFFERENT account than the one now signed
 *    in is cleared before anything reads it (reconcileAccountLocalState);
 *  - only state this code marked as a guest's is ever adopted by a sign-in.
 *
 * The marker always changes before the session cookie does, at sign-in and at
 * sign-out, and a page stops syncing and persisting what it loaded as soon as
 * the marker no longer matches (accountChangedSinceLoad): the cookie is shared
 * by every open tab, the in-memory state is not.
 *
 * The owner is the signed-in email, normalized exactly the way the chat sync
 * state key normalizes it (lib/chatSync/identity.ts), or GUEST_OWNER, and it
 * is written next to the state it vouches for. Dependency-free on purpose so
 * it can run before the app renders and be unit tested without a DOM.
 */

/** Which account the cached state below belongs to, or GUEST_OWNER. */
export const ACCOUNT_OWNER_KEY = 'baba.account.owner'

/**
 * The marker for state written while nobody was signed in, by this code. It is
 * the only state a sign-in adopts. State with no marker at all predates this
 * code, and nothing can say whose it was (see reconcileAccountLocalState). The
 * ':' keeps it apart from every normalized email.
 */
export const GUEST_OWNER = ':guest'

/** Exact localStorage keys that hold one account's data. */
export const ACCOUNT_LOCAL_KEYS: readonly string[] = [
  // Chat (same key names as the phone's chatStore; see gatewayConversationStore.ts).
  'chat_conversations',
  'chat_active_conversation_id',
  // Translator: recents, the last result and the draft in the box.
  'baba.app.recent_translations.v2',
  'baba.app.output.v1',
  'baba.app.output.v1.translit',
  'baba.app.draft.v1',
  // Translation settings (language pair, gender, voice). Settings pushes the
  // gender and default language into the account (lib/settingsSync.ts), so
  // another account's choices must not be the next one's.
  'baba.app.prefs.v1',
  // Caches of server answers for this account.
  'baba.app.last_subscription_status',
  'baba.app.last_usage',
  'baba.app.display_name',
  'baba.referral.code',
  // Progress (lib/progress/store.ts). firstOpen and percentiles are not the
  // account's: the first is this browser's own first visit, the second is a
  // global table.
  'baba.progress.translationCount',
  'baba.progress.lifetimeCharacters',
  'baba.progress.firstOpenServer',
  'baba.progress.highestBadgeCelebrated',
  'baba.progress.highestMasteryCelebrated',
  'baba.progress.highestPercentileCelebrated',
  'baba.progress.streak',
  // Slang Dictionary likes and word requests reconcile with the account.
  'baba.dictionary.liked',
  'baba.dictionary.requests',
  'baba.app.ulpan_waitlist_joined_email',
]

/**
 * Key prefixes that hold one account's data. `chat_sync_v1:` has to go with
 * `chat_conversations`: it is the pull cursor and the upload watermarks, and a
 * cursor that survives an emptied chat store tells the next sign-in of the same
 * account that it already has history it no longer holds. MIRROR:
 * CHAT_SYNC_STATE_KEY_PREFIX in lib/chatSync/state.ts.
 */
export const ACCOUNT_LOCAL_PREFIXES: readonly string[] = ['chat_sync_v1:', 'baba.convo.recap.']

export interface AccountStorage {
  readonly length: number
  key(index: number): string | null
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function defaultStorage(): AccountStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/** Same normalization as chatSyncIdentityKey, so the two can be compared. */
export function normalizeAccountOwner(email: string): string {
  return email.trim().toLowerCase().replace(/[^a-z0-9@._-]/g, '_').slice(0, 96)
}

function keysWithPrefix(storage: AccountStorage, prefix: string): string[] {
  const out: string[] = []
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i)
    if (key && key.startsWith(prefix)) out.push(key)
  }
  return out
}

/**
 * Remove every account-scoped key, then record who the (now empty) state
 * belongs to: a guest by default, which is what every sign-out path leaves
 * behind. Never throws: blocked storage means there is nothing to clear.
 */
export function clearAccountLocalState(
  storage: AccountStorage | null = defaultStorage(),
  nextOwner: string = GUEST_OWNER,
): void {
  if (!storage) return
  try {
    for (const key of ACCOUNT_LOCAL_KEYS) storage.removeItem(key)
    for (const prefix of ACCOUNT_LOCAL_PREFIXES) {
      for (const key of keysWithPrefix(storage, prefix)) storage.removeItem(key)
    }
    storage.setItem(ACCOUNT_OWNER_KEY, nextOwner)
  } catch {
    // Storage refused mid-way; the next load reconciles again.
  }
}

/** Record that the cached state now belongs to `email` (an email change keeps the account). */
export function setAccountLocalStateOwner(email: string, storage: AccountStorage | null = defaultStorage()): void {
  if (!storage || !email.trim()) return
  try {
    storage.setItem(ACCOUNT_OWNER_KEY, normalizeAccountOwner(email))
  } catch {}
}

export function readAccountLocalStateOwner(storage: AccountStorage | null = defaultStorage()): string | null {
  if (!storage) return null
  try {
    return storage.getItem(ACCOUNT_OWNER_KEY)
  } catch {
    return null
  }
}

let loadedOwner: string | null | undefined

/**
 * Who the cached state belonged to when this page loaded it, pinned on first
 * call (main.tsx calls it right after reconcileAccountLocalState). Chats and
 * recents read from storage at that moment stay that account's for the life
 * of the page, whatever session the cookie holds later.
 */
export function loadedStateOwner(storage: AccountStorage | null = defaultStorage()): string | null {
  if (loadedOwner === undefined) loadedOwner = readAccountLocalStateOwner(storage)
  return loadedOwner
}

/**
 * Whether the owner marker has moved since this page loaded its state: another
 * tab signed in, out, or as someone else, or this page is between its own
 * sign-in and the reload that follows. What the page holds in memory is still
 * the previous owner's, so from that moment it neither syncs it nor writes it
 * back under the new marker.
 */
export function accountChangedSinceLoad(storage: AccountStorage | null = defaultStorage()): boolean {
  return readAccountLocalStateOwner(storage) !== loadedStateOwner(storage)
}

/**
 * Whether what this page loaded may be synced into `identity`'s account: it
 * was a guest's (marked by this code), or already that account's, and the
 * marker still says so. Every sign-in reloads the page, but the session
 * cookie is shared by every tab and changes before any reload, and a sync in
 * that gap would upload the previous account's chats into the new one. A
 * sign-in moves the marker BEFORE it writes the cookie
 * (handAccountLocalStateTo), so a tab whose `identity` is still the old one
 * refuses from the moment the owner changes. No marker (storage that cannot
 * be written) is never assumed to be anyone's.
 */
export function loadedStateBelongsTo(identity: string, storage: AccountStorage | null = defaultStorage()): boolean {
  if (accountChangedSinceLoad(storage)) return false
  const owner = loadedStateOwner(storage)
  if (owner === GUEST_OWNER) return true
  return owner !== null && owner === normalizeAccountOwner(identity)
}

/**
 * Every sign-in calls this just BEFORE it writes the session cookie, with the
 * email the cookie will carry. The state is kept when it is already that
 * account's, adopted when it is a guest's, and otherwise (another account's,
 * or unmarked, whose owner nothing can name) cleared and recorded for it.
 */
export function handAccountLocalStateTo(
  email: string,
  storage: AccountStorage | null = defaultStorage(),
): Exclude<ReconcileOutcome, 'stamped'> {
  if (!storage || !email.trim()) return 'kept'
  let owner: string | null = null
  try {
    owner = storage.getItem(ACCOUNT_OWNER_KEY)
  } catch {
    return 'kept'
  }
  const me = normalizeAccountOwner(email)
  if (owner === me) return 'kept'
  if (owner === GUEST_OWNER) {
    setAccountLocalStateOwner(email, storage)
    return 'adopted'
  }
  clearAccountLocalState(storage, me)
  return 'cleared'
}

/**
 * Reload this tab whenever another tab changes whose state this is: a sign-in
 * (guest to account), a sign-out (account to guest), or a switch. The chats and
 * recents this tab holds in memory were loaded for the old owner, and it would
 * otherwise write them back, under the new owner's marker, for the new
 * account's sync to upload. The reloaded tab starts from the reconciled state.
 */
export function reloadWhenAccountOwnerChanges(
  target: { addEventListener(type: 'storage', listener: (event: { key: string | null }) => void): void },
  reload: () => void,
  storage: AccountStorage | null = defaultStorage(),
): void {
  const ownerAtLoad = loadedStateOwner(storage)
  target.addEventListener('storage', (event) => {
    // A null key is localStorage.clear() in another tab.
    if (event.key !== ACCOUNT_OWNER_KEY && event.key !== null) return
    if (readAccountLocalStateOwner(storage) !== ownerAtLoad) reload()
  })
}

export type ReconcileOutcome = 'kept' | 'adopted' | 'stamped' | 'cleared'

/**
 * Run once, before anything reads the cached state. `signedInEmail` is the
 * email of the CURRENT session, or null when nobody is signed in.
 *
 * Signed in:
 *  - recorded for this account: kept;
 *  - recorded as a guest's: adopted (guest work done before signing in);
 *  - recorded for another account: cleared, then recorded for this one;
 *  - no marker: the first load of this code in a session that was already
 *    looking at this state, so it is stamped as this account's, never wiped.
 *
 * Signed out:
 *  - any marker: kept (a lapsed session's state waits for its account; the
 *    next different sign-in clears it);
 *  - no marker: state from before this code existed, whose owner nothing can
 *    name. It is cleared AT THIS LOAD and the store starts over as a guest's,
 *    so it can never be adopted by a later sign-in, and everything the visitor
 *    does from here on is guest state that can be.
 */
export function reconcileAccountLocalState(
  signedInEmail: string | null,
  storage: AccountStorage | null = defaultStorage(),
): ReconcileOutcome {
  if (!storage) return 'kept'
  let owner: string | null = null
  try {
    owner = storage.getItem(ACCOUNT_OWNER_KEY)
  } catch {
    return 'kept'
  }
  if (!signedInEmail || !signedInEmail.trim()) {
    if (owner !== null) return 'kept'
    clearAccountLocalState(storage, GUEST_OWNER)
    return 'cleared'
  }
  const me = normalizeAccountOwner(signedInEmail)
  if (owner === me) return 'kept'
  if (owner === GUEST_OWNER) {
    setAccountLocalStateOwner(signedInEmail, storage)
    return 'adopted'
  }
  if (owner === null) {
    setAccountLocalStateOwner(signedInEmail, storage)
    return 'stamped'
  }
  clearAccountLocalState(storage, me)
  return 'cleared'
}
